import { Flex, VStack } from '@chakra-ui/react'
import React, { useRef, useState } from 'react'
import IconDropdownDownArrow from '~/assets/icons/icon-dropdown-downArrow.svg?react'
import IconDropdownUpArrow from '~/assets/icons/icon-dropdown-upArrow.svg?react'
import IconPhoneKeypad from '~/assets/svgs/icon-phone-keypad.svg?react'
import IconSearch from '~/assets/svgs/icon-search.svg?react'
import { useSettingsState } from '~/atoms/SettingsState'
import { useLastOutboundPhoneNumber } from '~/atoms/WebPhoneInteractionState'
import { MenuPlacement } from '~/components/shared/Dropdown/Menu'
import { SearchableDropdownTextButton } from '~/components/shared/Dropdown/SearchableDropdownTextButton'
import { TabButton } from '~/components/shared/TabButton'
import { useAnalytics } from '~/hooks/useAnalytics'
import useDebouncedCallback from '~/hooks/useDebounceCallback'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { useGetPhoneNumbers } from '~/queries/phoneNumberQueries'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'
import { SearchValue } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'

import { PhoneNumber } from '@mangovoice/types'

import { CallButton } from './CallButton'
import { CallButtonVariants } from './componentStyles/CallButton.style'
import { useKeypadStyles } from './componentStyles/Keypad.style'
import { ContactCardsList } from './ContactCardsList'
import { DeleteButton } from './DeleteButton'
import { Keypad } from './KeyPad'
import { PhoneNumberInput } from './PhoneNumberInput'
import { PhoneSearchInput } from './PhoneSearchInput'
import { isPhoneNumber } from './utils/stringUtils'

const DropdownDownArrow = (props: React.SVGProps<SVGSVGElement>) => (
  <IconDropdownDownArrow {...props} />
)
const DropdownUpArrow = (props: React.SVGProps<SVGSVGElement>) => (
  <IconDropdownUpArrow {...props} />
)

interface OutOfCallViewProps {
  activeTab: 'dialpad' | 'search'
  onChangeTab: (newTab: 'dialpad' | 'search') => void
  phoneNumber: string
  searchValue?: SearchValue
  searchedValue?: SearchValue
  isSearching?: boolean
  isInputDisabled?: boolean
  onSearchTermChange: (newSearchTerm: SearchValue) => void
  onPhoneNumberChange: (newNumber: string) => void
  searchResults?: ContactDetails[]
  onResultsEndReached?: () => void
  isSearchLoading?: boolean
  onStartCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
    outboundCallerIdName?: string,
  ) => void
  onDialButtonClick: (digit: string) => void
  onDeleteButtonClick: () => void
}
export const OutOfCallView = ({
  activeTab,
  onChangeTab: handleChangeTab,
  phoneNumber,
  searchValue,
  searchedValue,
  isSearching,
  isInputDisabled,
  onSearchTermChange: handleSearchTermChange,
  onPhoneNumberChange: handlePhoneNumberChange,
  searchResults,
  onResultsEndReached,
  isSearchLoading,
  onStartCall: handleStartCall,
  onDialButtonClick: handleDialButtonClick,
  onDeleteButtonClick,
}: OutOfCallViewProps) => {
  const analytics = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const [outboundSearch, setOutboundSearch] = useState<string>('')
  const { results: outboundPhoneNumbers, fetchNextPage } = useGetPhoneNumbers({
    search: outboundSearch,
  })
  const {
    settings: {
      webPhone: { enableCallerIdSelection },
    },
  } = useSettingsState()

  const [lastUsedOutboundPhone, setLastUsedOutboundPhone] =
    useLastOutboundPhoneNumber()
  const [deleteTimeout, setDeleteTimeout] = useState<NodeJS.Timeout | null>(
    null,
  )
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)

  if (deleteTimeout && !phoneNumber) {
    clearTimeout(deleteTimeout)
  }

  const handleDeleteButtonClick = () => {
    if (deleteTimeout) {
      clearTimeout(deleteTimeout)
    }
    onDeleteButtonClick()
  }
  const handleHoldDelete = () => {
    if (deleteTimeout) {
      clearTimeout(deleteTimeout)
    }
    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        onDeleteButtonClick()
      }, 150)
      setDeleteTimeout(interval)
    }, 250)
    setDeleteTimeout(timeout)
  }

  const handleMenuOpenChange = () => {
    setMenuOpen(!menuOpen)
    return !menuOpen
  }

  const handleOutboundSearchChanged = useDebouncedCallback((search: string) => {
    analytics.track(WebPhoneAnalyticsEvents.OutboundNumberSearch, {
      scope: 'out_of_call_phone',
      searchValue: search,
    })
    setOutboundSearch(search)
  }, 300)

  const handleOutboundItemSelect = (item: PhoneNumber) => {
    analytics.track(WebPhoneAnalyticsEvents.OutboundNumberSelection, {
      scope: 'out_of_call_phone',
      selection: item,
    })
    setLastUsedOutboundPhone(item)
    setOutboundSearch('')
  }

  const canMakeCall = isPhoneNumber(phoneNumber) && phoneNumber.length >= 2

  const keypadSize = { base: Size.sm, lg: Size.md, xl: Size.lg } as const
  const styles = useKeypadStyles({
    size: keypadSize,
  })

  return (
    <VStack width={'auto'}>
      <VStack
        alignSelf="center"
        align="center"
        w={{ base: '23.75rem', lg: '25rem', xl: '30rem' }}
        h="85%"
        gap="1rem"
      >
        {enableCallerIdSelection && (
          <Flex>
            <SearchableDropdownTextButton
              defaultItem={
                lastUsedOutboundPhone
                  ? {
                      value: lastUsedOutboundPhone,
                      content: `${lastUsedOutboundPhone?.name}: ${formatPhoneNumber(
                        lastUsedOutboundPhone?.number,
                      )}`,
                    }
                  : undefined
              }
              items={outboundPhoneNumbers.map((item) => {
                return {
                  value: item,
                  content: `${item.name}: ${formatPhoneNumber(item.number)}`,
                }
              })}
              onSelect={handleOutboundItemSelect}
              onMenuOpenChange={handleMenuOpenChange}
              onScrollEndReached={fetchNextPage}
              onSearchChanged={handleOutboundSearchChanged}
              rightIcon={menuOpen ? DropdownUpArrow : DropdownDownArrow}
              menuOptions={{
                width: '25rem',
                placement: MenuPlacement.BottomCenter,
              }}
            />
          </Flex>
        )}
        {activeTab === 'dialpad' && (
          <PhoneNumberInput
            phonenumber={formatPhoneNumber(phoneNumber)}
            outboundCallerIdName={lastUsedOutboundPhone?.number}
            onPhonenumberChange={handlePhoneNumberChange}
            onStartCall={handleStartCall}
            inputRef={inputRef}
            disabled={isInputDisabled || menuOpen}
            callDisabled={!canMakeCall}
          />
        )}
        {activeTab === 'search' && (
          <PhoneSearchInput
            onSearchChange={handleSearchTermChange}
            disabled={isInputDisabled || menuOpen}
            searchInputValue={searchValue?.label ?? ''}
          />
        )}
        <Flex w="full" justify="center" gap="0.25rem">
          <TabButton
            leftIcon={IconPhoneKeypad}
            text="Dialpad"
            isActive={activeTab === 'dialpad'}
            onClick={() => handleChangeTab('dialpad')}
          />
          <TabButton
            leftIcon={IconSearch}
            text="Search"
            isActive={activeTab === 'search'}
            onClick={() => handleChangeTab('search')}
          />
        </Flex>
        {activeTab === 'dialpad' && (
          <Flex w="full" justify="center">
            <Keypad
              pt="2.5rem"
              size={keypadSize}
              onButtonClick={handleDialButtonClick}
              centerButton={
                <CallButton
                  disabled={!canMakeCall}
                  onClick={() =>
                    handleStartCall(phoneNumber, lastUsedOutboundPhone?.number)
                  }
                  variant={CallButtonVariants.Start}
                  size={Size.full}
                  sx={styles.callButton}
                />
              }
              rightButton={
                <DeleteButton
                  display={phoneNumber ? 'initial' : 'none'}
                  handleDelete={handleDeleteButtonClick}
                  handleHoldDelete={handleHoldDelete}
                />
              }
            />
          </Flex>
        )}
        {activeTab === 'search' && (
          <Flex direction="column" w="full" h="full">
            <ContactCardsList
              searchContacts={searchResults}
              isSearching={isSearching}
              searchValue={searchedValue}
              onSearchEndReached={onResultsEndReached}
              isSearchLoading={isSearchLoading}
              onStartCall={handleStartCall}
            />
          </Flex>
        )}
      </VStack>
    </VStack>
  )
}
